import { alterInTagen } from './zeit'

/**
 * **Wie es um ein Vorhaben steht** (26.08.2026, Entwurf `infos/vorhaben-standalone.html`).
 *
 * Die Vorhaben-Seite zeigte bis dahin jedes Vorhaben als gleich wichtige Karte: „IOR 2.0 · 1/1 ·
 * laufend" und „NetCon Construction · 0/0 · Ziel 31.10." standen gleichberechtigt untereinander,
 * obwohl das eine läuft und das andere noch keine einzige Aufgabe hat. Wer die Liste überfliegt,
 * kann daraus nicht ablesen, wo er hinschauen muss.
 *
 * Der Zustand ist die Antwort ([E-254](../../../docs/ENTSCHEIDUNGEN.md#e-254),
 * [E-255](../../../docs/ENTSCHEIDUNGEN.md#e-255)): **ein Wort je Vorhaben, aus den Daten
 * gerechnet.** Er wird nicht gespeichert und nicht von Hand gesetzt – ein Zustandsfeld bräuchte
 * einen Auftrag, der es umschaltet, und ein Wecker, der einmal nicht läuft, behauptete wochenlang
 * „zieht". Derselbe Grund wie beim Pinzustand ([E-216](../../../docs/ENTSCHEIDUNGEN.md#e-216)).
 *
 * Alles hier ist rein und ohne `new Date()` ohne Übergabe: „steht" ist die eine Regel, deren
 * Antwort von *morgen* abhängt, und ohne setzbaren Stichtag ließe sie sich nur durch Warten
 * prüfen.
 */

/* ─────────────────────────── Die sechs Zustände ───────────────────────────── */

/**
 * **Reihenfolge nach Dringlichkeit**, nicht alphabetisch – die Liste gruppiert danach, und was
 * oben steht, will heute etwas von jemandem.
 */
export const VORHABEN_ZUSTAENDE = ['trocken', 'steht', 'leer', 'zieht', 'ruhig', 'fertig'] as const
export type VorhabenZustand = (typeof VORHABEN_ZUSTAENDE)[number]

export const VORHABEN_ZUSTAND_LABELS: Record<VorhabenZustand, string> = {
  trocken: 'trocken',
  steht: 'steht',
  leer: 'leer',
  zieht: 'zieht',
  ruhig: 'ruhig',
  fertig: 'fertig',
}

/**
 * Was der Zustand dem Owner sagt – steht als Erklärung neben dem Gruppenkopf.
 *
 * **Jeder Satz nennt die Abhilfe, nicht den Befund.** „Keine Bewegung seit drei Wochen" ist eine
 * Messung; „nachfragen oder pausieren" ist das, was man damit tut.
 */
export const VORHABEN_ZUSTAND_ERKLAERUNG: Record<VorhabenZustand, string> = {
  trocken: 'Aufgaben sind da, aber sie erreichen keinen Entwickler. Pool und Nachfluss einstellen.',
  steht: 'Seit über drei Wochen keine Bewegung – nachfragen oder stilllegen.',
  leer: 'Noch keine Aufgabe angelegt. Zerlegen oder streichen.',
  zieht: 'Läuft. Hier ist nichts zu tun.',
  ruhig: 'Stillgelegt. Bleibt sichtbar, verlangt aber nichts.',
  fertig: 'Alle Aufgaben erledigt.',
}

/**
 * **Ab wann ein Vorhaben „steht".**
 *
 * Drei Wochen, nicht zwei: ein Vorhaben ist kein Ticket. Urlaub, eine Woche Störungsdienst und
 * ein Feiertag ergeben zusammen mühelos vierzehn Tage, in denen zu Recht nichts passiert – bei
 * dieser Schwelle wäre die halbe Liste dauerhaft im Alarmzustand, und ein Alarm, der immer
 * leuchtet, wird abgeschaltet.
 *
 * ## Nicht zu verwechseln mit `STEHT_AB_TAGEN` aus `meintag.ts`
 *
 * Dort steht dasselbe Wort für **fünf** Tage – und das ist kein Widerspruch, sondern eine
 * andere Frage. „Mein Tag" zeigt die Vorhaben-Spur eines *Tages*: „bewegt sich / ruhig / steht"
 * beantwortet, woran gerade gearbeitet wird, und dafür ist eine Woche ohne Regung lang. Diese
 * Seite beantwortet, was **vergessen** wurde, und dafür ist eine Woche nichts.
 *
 * Beide Zahlen zusammenzulegen hieße, eine der beiden Fragen falsch zu beantworten. Ob sie
 * dauerhaft getrennt bleiben: [F-77](../../../docs/OFFENE-FRAGEN.md).
 */
export const VORHABEN_STEHT_AB_TAGEN = 21

/* ─────────────────────────── Die Lage eines Vorhabens ─────────────────────── */

/**
 * Was gebraucht wird, um den Zustand zu rechnen – strukturell, damit eine Zeile aus
 * `vorhaben.alle` unverändert hineinpasst.
 */
export type VorhabenLage = {
  /** `false` heißt stillgelegt – eine menschliche Entscheidung, kein Befund. */
  aktiv: boolean
  poolId: string | null
  nachflussLimit: number | null
  /**
   * **Ob dieses Vorhaben nachfließen *soll*** ([E-256](../../../docs/ENTSCHEIDUNGEN.md#e-256)).
   *
   * `false` schaltet den Befund „trocken" ab – nicht den Nachfluss selbst, der hängt weiter an
   * Pool und Grenze. Siehe `vorhaben.nachfluss_gewuenscht`.
   */
  nachflussGewuenscht: boolean
  stand: { gesamt: number; erledigt: number; offen: number }
  /**
   * Wann zuletzt etwas an diesem Vorhaben geschah – jüngste Änderung an einer seiner Aufgaben,
   * jüngster Kommentar, sonst die eigene Änderung.
   *
   * **`Date | string`, weil der tRPC-Client keinen Transformer hat**: der Typ aus dem Router sagt
   * `Date`, über die Leitung kommt die ISO-Zeichenkette aus `JSON.stringify`. Wer hier `Date`
   * verlangte, bekäme einen Typecheck, der grün ist, und ein `getTime is not a function` im
   * Browser. Dieselbe Stelle, an der die Ansicht seit jeher `new Date(v.zieltermin)` schreibt.
   *
   * `null` heißt „nicht bekannt" und schließt „steht" aus – ein Befund ohne Grundlage ist
   * schlechter als kein Befund.
   */
  letzteBewegung: Date | string | null
}

/** Nimmt beides, gibt `null` bei Unbrauchbarem – siehe {@link VorhabenLage.letzteBewegung}. */
export function alsDatum(wert: Date | string | null | undefined): Date | null {
  if (wert === null || wert === undefined) return null
  const d = wert instanceof Date ? wert : new Date(wert)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * **Fließt aus diesem Vorhaben überhaupt etwas nach?**
 *
 * Drei Bedingungen: ohne Pool weiß der Nachfluss nicht wohin, ohne Limit ist er ausdrücklich
 * ausgeschaltet (`null` heißt „kein Nachfluss", siehe `vorhaben.nachfluss_limit`), und
 * abgewählt ist abgewählt. Ein Limit von `0` ist dasselbe in Grün – es schiebt nie.
 *
 * **`nachflussGewuenscht` gehört hierher und nicht nur in die Zustandsprüfung.** Sonst gäbe es
 * einen Fall, in dem Oberfläche und Technik auseinanderlaufen: Pool gesetzt, Grenze gesetzt,
 * Häkchen abgewählt. `nachflussPlanen` überspringt das Vorhaben, die Zeile hätte aber „zieht"
 * gezeigt und die Marke einen gefüllten Bestand – eine Anzeige, die etwas verspricht, was der
 * Server nicht tut.
 */
export function nachflussLaeuft(
  lage: Pick<VorhabenLage, 'poolId' | 'nachflussLimit' | 'nachflussGewuenscht'>,
): boolean {
  if (!lage.nachflussGewuenscht) return false
  return lage.poolId !== null && lage.nachflussLimit !== null && lage.nachflussLimit > 0
}

/**
 * **Der Zustand eines Vorhabens zu einem gegebenen Zeitpunkt.**
 *
 * ## Warum „trocken" vor „steht" geprüft wird
 *
 * Der Entwurf stellte es andersherum. Das kippt nach drei Wochen von selbst ins Falsche: ein
 * Vorhaben ohne Nachfluss **kann** keine Bewegung haben – niemand bekommt seine Aufgaben zu
 * sehen –, also wird jedes trockene Vorhaben irgendwann „steht". Damit verschwände genau in dem
 * Moment die Ursache aus der Anzeige, in dem sie am längsten besteht.
 *
 * „Trocken" nennt die Ursache und hat eine Abhilfe, die eine Minute dauert. „Steht" ist das
 * Symptom und schickt jemanden ins Nachfragen. Wenn beides zutrifft, ist die Ursache die
 * nützlichere Auskunft.
 *
 * ## Warum „trocken" nicht am Poolbestand hängt
 *
 * Naheliegend wäre `imPool === 0` gewesen. Das heißt bei uns aber nicht „der Nachfluss steht" –
 * es heißt genauso oft, dass die nachgeflossenen Aufgaben schon **gezogen** wurden. Das ist der
 * Normalfall eines gut laufenden Vorhabens, und die Seite hätte Alarm geschlagen, wo alles
 * funktioniert.
 *
 * ## Und warum „trocken" abwählbar ist
 *
 * Nils, 26.08.2026: *„trocken ist nicht immer ein Fehler."* Es gibt Vorhaben, die bewusst ohne
 * Nachfluss laufen – eine Sammelstelle, ein Ablage-Thema. Ohne `nachflussGewuenscht` stünden die
 * dauerhaft im obersten Block, und nach der dritten Woche liest den niemand mehr
 * ([E-256](../../../docs/ENTSCHEIDUNGEN.md#e-256)).
 *
 * **Abgewählt heißt nicht „unsichtbar":** ein solches Vorhaben kann weiterhin „steht" werden.
 * Auch eine Ablage kann vergessen werden – wer *gar* keine Meldung will, legt das Vorhaben still
 * (`aktiv = false` → „ruhig").
 */
export function vorhabenZustand(lage: VorhabenLage, jetzt: Date): VorhabenZustand {
  /** Stillgelegtes meldet nichts. Wer es abgeschaltet hat, weiß, dass es steht. */
  if (!lage.aktiv) return 'ruhig'

  if (lage.stand.gesamt > 0 && lage.stand.erledigt >= lage.stand.gesamt) return 'fertig'

  /**
   * **Ohne Aufgaben gibt es nichts zu beurteilen** – weder Nachfluss noch Bewegung. „Leer" ist
   * auch für ein frisch angelegtes Vorhaben die ehrliche Auskunft: es zieht nicht.
   */
  if (lage.stand.gesamt === 0) return 'leer'

  if (lage.stand.offen > 0 && lage.nachflussGewuenscht && !nachflussLaeuft(lage)) return 'trocken'

  const bewegung = alsDatum(lage.letzteBewegung)
  if (
    lage.stand.offen > 0 &&
    bewegung !== null &&
    alterInTagen(bewegung, jetzt) > VORHABEN_STEHT_AB_TAGEN
  ) {
    return 'steht'
  }

  return 'zieht'
}

/** Wie viele Tage ohne Bewegung – für die Zeile („steht seit 34 Tagen"). `null`, wenn unbekannt. */
export function tageOhneBewegung(lage: VorhabenLage, jetzt: Date): number | null {
  const bewegung = alsDatum(lage.letzteBewegung)
  return bewegung === null ? null : alterInTagen(bewegung, jetzt)
}

/**
 * Zählt je Zustand, in der Reihenfolge von {@link VORHABEN_ZUSTAENDE}. Zustände ohne Vorhaben
 * kommen **nicht** vor: eine Filterleiste mit „0 trocken" lädt zum Klicken auf eine leere Liste
 * ein.
 */
export function zustandsZaehlung(
  lagen: readonly VorhabenLage[],
  jetzt: Date,
): { zustand: VorhabenZustand; anzahl: number }[] {
  const zaehler = new Map<VorhabenZustand, number>()
  for (const l of lagen) {
    const z = vorhabenZustand(l, jetzt)
    zaehler.set(z, (zaehler.get(z) ?? 0) + 1)
  }
  return VORHABEN_ZUSTAENDE.filter((z) => (zaehler.get(z) ?? 0) > 0).map((z) => ({
    zustand: z,
    anzahl: zaehler.get(z) ?? 0,
  }))
}
